import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BaseButton, Checkbox, RadioButton, useT } from '@repo/common';
import { versionPath } from '../../../pages/Policy/policyRegistry';
import HeaderText from '../Common/HeaderText';
import BilingualListField from './BilingualListField';
import SendPolicyNoticeModal from './SendPolicyNoticeModal';
import {
  AUDIENCES,
  campaignKeyOf,
  daysBetween,
  deriveCampaignKey,
  formatDateEn,
  formatDateKo,
  initialForm,
  SENDER_DOMAINS,
  toPayload,
  TWO_DAYS_AUDIENCE,
  TWO_DAYS_BRAND,
  validateForm,
} from './policyNoticeForm';
import {
  getPolicyNoticeStatus,
  previewPolicyNotice,
  sendPolicyNotice,
  sendPolicyNoticeTest,
} from '../../../api/policyNotice/policyNotice.api';

// 어드민 약관·개인정보처리방침 개정 안내 메일 작성·발송
const DRAFT_KEY = 'adminPolicyNoticeDraft';
const BATCH_SIZE = 10;
const DEFAULT_CAP = 300;
const MAX_CAP = 2000;
const MAX_PER_REQUEST = 500;

const INPUT_CLASS =
  'w-full h-[40px] rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 px-[12px] typo-b14 text-text-primary placeholder:text-text-placeholder focus:outline-none focus:border-grayscale-gray04 disabled:bg-grayscale-gray01';

const loadDraft = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    return saved ? { ...initialForm(), ...saved } : initialForm();
  } catch {
    return initialForm();
  }
};

const clampCap = (value) =>
  Math.max(1, Math.min(MAX_CAP, Math.floor(Number(value)) || DEFAULT_CAP));

const errorMessage = (error, fallback) =>
  error?.response?.data?.msg || error?.response?.data?.message || fallback;

const Section = ({ title, description, children }) => (
  <section className='flex flex-col gap-[16px] rounded-[16px] border border-grayscale-gray02 bg-grayscale-gray00 p-[20px] tabletH:p-[24px]'>
    <div className='flex flex-col gap-[4px]'>
      <h3 className='typo-b16Strong text-text-primary'>{title}</h3>
      {description && (
        <p className='typo-b13 text-text-placeholder'>{description}</p>
      )}
    </div>
    {children}
  </section>
);

const Field = ({ label, hint, children }) => (
  <label className='flex flex-col gap-[6px] min-w-0'>
    <span className='typo-b13Strong text-text-secondary'>{label}</span>
    {children}
    {hint && <span className='typo-b12 text-text-placeholder'>{hint}</span>}
  </label>
);

const Banner = ({ tone, children }) => {
  const toneClass = {
    error: 'bg-functional-failed01 text-functional-failed03',
    warning: 'bg-functional-warning01 text-text-primary',
    success: 'bg-green-light02 text-green-deep04',
  }[tone];
  return (
    <div className={`rounded-[10px] px-[14px] py-[10px] typo-b13 ${toneClass}`}>
      {children}
    </div>
  );
};

const AdminPolicyNotice = () => {
  const t = useT();
  const [form, setForm] = useState(loadDraft);
  const [errors, setErrors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusTick, setStatusTick] = useState(0);
  const [cap, setCap] = useState(DEFAULT_CAP);
  const [confirm, setConfirm] = useState(null);
  const [run, setRun] = useState(null);
  const stopRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const campaignKey = campaignKeyOf(form);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const doc = params.get('noticeDoc');
    const date = params.get('noticeDate');
    if (!doc || !date) return;

    const privacyDoc = doc.endsWith('privacy');
    const twoDays = doc.startsWith('2days-');
    const path = twoDays
      ? `/2days/${privacyDoc ? 'privacy' : 'terms'}?version=${date}`
      : versionPath(doc, date);
    setForm((prev) => {
      const brand = prev.brandName.trim();
      return {
        ...prev,
        audience: twoDays ? TWO_DAYS_AUDIENCE : AUDIENCES[0].value,
        brandName: twoDays ? brand || TWO_DAYS_BRAND : brand === TWO_DAYS_BRAND ? '' : prev.brandName,
        terms: !privacyDoc,
        privacy: privacyDoc,
        effectiveDate: date,
        archiveDate: '',
        campaignKey: '',
        noticeUrl: `${window.location.origin}${path}`,
      };
    });
    params.delete('noticeDoc');
    params.delete('noticeDate');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const running = !!run?.running;
  const gapDays =
    form.noticeDate && form.effectiveDate
      ? daysBetween(form.noticeDate, form.effectiveDate)
      : null;

  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {}
  }, [form]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      previewPolicyNotice(toPayload(form))
        .then((result) => {
          if (cancelled) return;
          setPreview(result);
          setPreviewFailed(false);
        })
        .catch(() => !cancelled && setPreviewFailed(true));
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form]);

  useEffect(() => {
    if (!campaignKey) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      getPolicyNoticeStatus(campaignKey, form.audience)
        .then((result) => !cancelled && setStatus(result))
        .catch(() => !cancelled && setStatus(null));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [campaignKey, form.audience, statusTick]);

  useEffect(() => {
    if (!running) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const selectAudience = (audience) =>
    setForm((prev) => {
      const brand = prev.brandName.trim();
      let { brandName } = prev;
      if (audience === TWO_DAYS_AUDIENCE && !brand) brandName = TWO_DAYS_BRAND;
      if (audience !== TWO_DAYS_AUDIENCE && brand === TWO_DAYS_BRAND) brandName = '';
      return { ...prev, audience, brandName };
    });

  const audienceLabel = (value) => {
    const option = AUDIENCES.find((item) => item.value === value) || AUDIENCES[0];
    return t(option.en, option.ko);
  };

  const checkForm = () => {
    const problems = validateForm(form);
    setErrors(problems);
    return problems.length === 0;
  };

  const handleReset = () => {
    if (
      !window.confirm(
        t('Clear everything you wrote?', '작성한 내용을 모두 지우고 새로 작성할까요?'),
      )
    ) {
      return;
    }
    setForm(initialForm());
    setErrors([]);
    setNotice(null);
    setRun(null);
  };

  const handleTestSend = async () => {
    if (!checkForm()) return;
    setBusy('test');
    setNotice(null);
    try {
      const to = await sendPolicyNoticeTest(toPayload(form));
      setNotice({
        tone: 'success',
        text: t(`Test email sent to ${to}.`, `${to} 로 테스트 메일을 보냈어요.`),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: errorMessage(
          error,
          t('Could not send the test email.', '테스트 메일을 보내지 못했어요.'),
        ),
      });
    } finally {
      setBusy(null);
    }
  };

  const handlePrepareSend = async () => {
    if (!checkForm()) return;
    const payload = toPayload(form);
    const limit = clampCap(cap);
    setCap(limit);
    setBusy('prepare');
    setNotice(null);
    try {
      const [rendered, dryRun] = await Promise.all([
        previewPolicyNotice(payload),
        sendPolicyNotice({
          ...payload,
          dryRun: true,
          limit: Math.min(limit, MAX_PER_REQUEST),
        }),
      ]);
      if (dryRun.remaining === 0) {
        setNotice({
          tone: 'success',
          text: t(
            'Every member has already received this campaign.',
            '이 발송 키로 모든 회원이 이미 메일을 받았어요.',
          ),
        });
        return;
      }
      setConfirm({
        payload,
        limit,
        summary: {
          audience: audienceLabel(payload.audience),
          from: rendered.from,
          subject: rendered.subject,
          effectiveDate: `${payload.effectiveDateKo} · ${payload.effectiveDateEn}`,
          campaignKey: payload.campaignKey,
          pending: dryRun.remaining,
          willSend: Math.min(limit, dryRun.remaining),
        },
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: errorMessage(
          error,
          t('Could not check the recipients.', '발송 대상을 확인하지 못했어요.'),
        ),
      });
    } finally {
      setBusy(null);
    }
  };

  const startSending = async () => {
    const { payload, limit, summary } = confirm;
    setConfirm(null);
    stopRef.current = false;

    let progress = {
      running: true,
      sent: 0,
      failed: 0,
      target: summary.willSend,
      remaining: summary.pending,
      failedSamples: [],
      stopReason: null,
      errorText: null,
    };
    setRun(progress);

    try {
      while (progress.sent < limit) {
        if (stopRef.current) {
          progress = { ...progress, stopReason: 'stopped' };
          break;
        }
        const result = await sendPolicyNotice({
          ...payload,
          limit: Math.min(BATCH_SIZE, limit - progress.sent),
        });
        progress = {
          ...progress,
          sent: progress.sent + result.sent,
          failed: progress.failed + result.failed,
          remaining: result.remaining,
          failedSamples: [...progress.failedSamples, ...result.failedSamples].slice(
            0,
            20,
          ),
        };
        setRun(progress);
        if (result.remaining === 0) {
          progress = { ...progress, stopReason: 'done' };
          break;
        }
        if (result.sent === 0) {
          progress = { ...progress, stopReason: 'stalled' };
          break;
        }
      }
      if (!progress.stopReason) progress = { ...progress, stopReason: 'cap' };
    } catch (error) {
      progress = {
        ...progress,
        stopReason: 'error',
        errorText: errorMessage(error, t('The request failed.', '요청이 실패했어요.')),
      };
    }

    setRun({ ...progress, running: false });
    setStatusTick((n) => n + 1);
  };

  const stopMessage = (state) => {
    switch (state.stopReason) {
      case 'done':
        return [
          'success',
          t('Every member has received the notice.', '모든 회원에게 발송을 마쳤어요.'),
        ];
      case 'cap':
        return [
          'warning',
          t(
            `Sent the ${state.sent} planned for now. Continue the remaining ${state.remaining} later with the same campaign key.`,
            `이번에 보낼 ${state.sent}명을 다 보냈어요. 남은 ${state.remaining}명은 내일 같은 발송 키로 이어서 보내 주세요.`,
          ),
        ];
      case 'stalled':
        return [
          'error',
          t(
            'A whole batch failed, so sending stopped. The daily Gmail limit may have been reached — check the reasons below.',
            '한 묶음이 전부 실패해서 멈췄어요. Gmail 일일 발송 한도를 넘었을 수 있으니 아래 실패 사유를 확인해 주세요.',
          ),
        ];
      case 'stopped':
        return [
          'warning',
          t(
            'Stopped. You can resume any time with the same campaign key.',
            '중지했어요. 같은 발송 키로 언제든 이어서 보낼 수 있어요.',
          ),
        ];
      case 'error':
        return [
          'error',
          `${state.errorText} ${t(
            'Wait a minute or two, then check the status and resume.',
            '1~2분 뒤 발송 현황을 새로고침하고 이어서 보내 주세요.',
          )}`,
        ];
      default:
        return null;
    }
  };

  const finished = run && !run.running ? stopMessage(run) : null;
  const percent = run?.target
    ? Math.min(100, Math.round((run.sent / run.target) * 100))
    : 0;

  return (
    <div className='flex flex-col min-w-0 w-full items-start'>
      <HeaderText label='Policy Notice' />

      <div className='grid w-full gap-[24px] desktopL:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start'>
        <div className='flex flex-col gap-[16px] min-w-0'>
          <fieldset disabled={running} className='flex flex-col gap-[16px] min-w-0'>
            <Section title={t('Recipients', '받는 사람')}>
              <div role='radiogroup' className='grid gap-[8px] tabletH:grid-cols-2'>
                {AUDIENCES.map((option) => {
                  const selected = form.audience === option.value;
                  return (
                    <div
                      key={option.value}
                      onClick={() => !running && selectAudience(option.value)}
                      className={`flex items-start gap-[10px] rounded-[12px] border px-[14px] py-[12px] cursor-pointer ${
                        selected
                          ? 'border-functional-success03 bg-green-light02'
                          : 'border-grayscale-gray02 bg-grayscale-gray00'
                      }`}
                    >
                      <RadioButton
                        checked={selected}
                        onChange={() => selectAudience(option.value)}
                        ariaLabel={t(option.en, option.ko)}
                        disabled={running}
                      />
                      <div className='flex flex-col gap-[2px]'>
                        <span className='typo-b14Strong text-text-primary'>
                          {t(option.en, option.ko)}
                        </span>
                        <span className='typo-b12 text-text-placeholder'>
                          {t(option.descEn, option.descKo)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section
              title={t('What changed', '개정 대상')}
              description={t(
                'The email wording, subject and button link follow what you pick here.',
                '선택한 문서에 맞춰 메일 문구·제목·버튼 링크가 바뀌어요.',
              )}
            >
              <div className='flex flex-wrap gap-[20px]'>
                <Checkbox
                  checked={form.terms}
                  onChange={(e) => update({ terms: e.target.checked })}
                  label={t('Terms of Use', '이용약관')}
                  labelClassName='text-text-primary'
                />
                <Checkbox
                  checked={form.privacy}
                  onChange={(e) => update({ privacy: e.target.checked })}
                  label={t('Privacy Policy', '개인정보처리방침')}
                  labelClassName='text-text-primary'
                />
              </div>
            </Section>

            <Section
              title={t('Brand & sender', '브랜드·보내는 사람')}
              description={t(
                'Set these per site, e.g. Koready Skills · skills@ko-ready.com.',
                '사이트에 맞춰 정해 주세요. 예: Koready Skills · skills@ko-ready.com',
              )}
            >
              <div className='grid gap-[12px] tabletH:grid-cols-2'>
                <Field
                  label={t('Brand name', '브랜드명')}
                  hint={t(
                    'Used in the greeting, sign-off, subject and footer. Blank = Koready.',
                    '인사말·맺음말·제목·하단에 들어가요. 비우면 Koready',
                  )}
                >
                  <input
                    type='text'
                    value={form.brandName}
                    onChange={(e) => update({ brandName: e.target.value })}
                    placeholder='Koready'
                    maxLength={40}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label={t('Sender name', '보내는 이름')}
                  hint={t('Blank = brand name', '비우면 브랜드명')}
                >
                  <input
                    type='text'
                    value={form.senderName}
                    onChange={(e) => update({ senderName: e.target.value })}
                    placeholder={form.brandName.trim() || 'Koready'}
                    maxLength={40}
                    className={INPUT_CLASS}
                  />
                </Field>
                <div className='flex flex-col gap-[6px] min-w-0 tabletH:col-span-2'>
                  <span className='typo-b13Strong text-text-secondary'>
                    {t('Sender address', '보내는 주소')}
                  </span>
                  <div className='flex items-center gap-[6px]'>
                    <input
                      type='text'
                      value={form.senderLocal}
                      onChange={(e) => update({ senderLocal: e.target.value })}
                      placeholder='skills'
                      aria-label={t('Address before @', '@ 앞 주소')}
                      className={`${INPUT_CLASS} flex-1 min-w-0`}
                    />
                    <span className='typo-b14 text-text-placeholder'>@</span>
                    <select
                      value={form.senderDomain}
                      onChange={(e) => update({ senderDomain: e.target.value })}
                      aria-label={t('Domain', '도메인')}
                      className={`${INPUT_CLASS} flex-1 min-w-0`}
                    >
                      {SENDER_DOMAINS.map((domain) => (
                        <option key={domain} value={domain}>
                          {domain}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <p className='typo-b12 text-text-placeholder'>
                {t(
                  "Only addresses added under \"Send mail as\" in the sending Gmail account show up as-is; others are replaced with the account's own address. Check with a test email.",
                  '발송 Gmail 계정의 "다른 주소에서 메일 보내기"에 등록된 주소만 그대로 보여요. 등록 안 된 주소는 계정 주소로 바뀌어 나가니 테스트 메일로 확인해 주세요.',
                )}
              </p>
            </Section>

            <Section title={t('Dates', '일정')}>
              <div className='grid gap-[12px] tabletH:grid-cols-3'>
                <Field label={t('Notice date', '공지일')}>
                  <input
                    type='date'
                    value={form.noticeDate}
                    onChange={(e) => update({ noticeDate: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field label={t('Effective date', '시행일')}>
                  <input
                    type='date'
                    value={form.effectiveDate}
                    onChange={(e) => update({ effectiveDate: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label={t('Old/new versions viewable from', '개정 전후 열람 가능일')}
                  hint={t('Blank = effective date', '비우면 시행일')}
                >
                  <input
                    type='date'
                    value={form.archiveDate}
                    onChange={(e) => update({ archiveDate: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </Field>
              </div>
              {form.effectiveDate && (
                <p className='typo-b12 text-text-placeholder'>
                  {t('Shown in the email as', '메일 표기')}:{' '}
                  {formatDateKo(form.effectiveDate)} · {formatDateEn(form.effectiveDate)}
                </p>
              )}
              {gapDays !== null && gapDays >= 0 && gapDays < 7 && (
                <Banner tone='warning'>
                  {t(
                    `Only ${gapDays} day(s) between the notice and the effective date. Notices usually go out 7 days ahead (30 days if the change is unfavorable to users).`,
                    `공지일부터 시행일까지 ${gapDays}일이에요. 보통 시행 7일 전(이용자에게 불리한 변경은 30일 전)까지 알려야 해요.`,
                  )}
                </Banner>
              )}
            </Section>

            <Section
              title={t('Email content', '메일 내용')}
              description={t(
                'Each line goes into both the Korean and English halves of the email.',
                '항목마다 한국어·영어를 함께 적어 주세요. 메일 한 통에 한국어 안내와 영문 안내가 차례로 들어가요.',
              )}
            >
              <Field
                label={t('Subject', '제목')}
                hint={t(
                  'Leave blank to use the default for the documents you picked.',
                  '비워두면 개정 대상에 맞는 기본 제목을 써요.',
                )}
              >
                <input
                  type='text'
                  value={form.subject}
                  onChange={(e) => update({ subject: e.target.value })}
                  placeholder={preview?.subject || ''}
                  className={INPUT_CLASS}
                />
              </Field>

              <BilingualListField
                label={t('Reason for update', '개정 사유')}
                rows={form.reasons}
                onChange={(reasons) => update({ reasons })}
                placeholder={{
                  ko: '예: 신규 기능 도입에 따른 정보 취급 규정 신설',
                  en: 'e.g. New rules for a newly launched feature',
                }}
              />
              {form.terms && (
                <BilingualListField
                  label={t('Key changes — Terms of Use', '주요 변경 사항 — 이용약관')}
                  rows={form.termsChanges}
                  onChange={(termsChanges) => update({ termsChanges })}
                  placeholder={{
                    ko: '예: 제8조. 서비스 이용 제한 사유 추가',
                    en: 'e.g. Art. 8. Added grounds for restricting use',
                  }}
                />
              )}
              {form.privacy && (
                <BilingualListField
                  label={t(
                    'Key changes — Privacy Policy',
                    '주요 변경 사항 — 개인정보처리방침',
                  )}
                  rows={form.privacyChanges}
                  onChange={(privacyChanges) => update({ privacyChanges })}
                  placeholder={{
                    ko: '예: 제2조. 수집 항목에 프로필 사진(선택) 추가',
                    en: 'e.g. Art. 2. Profile photo (optional) added',
                  }}
                />
              )}

              <Field
                label={t('Button link', '버튼 링크')}
                hint={t(
                  'Leave blank to open the policy page (/terms, or /privacy when only the Privacy Policy changed; under /2days for 2days participants).',
                  '비워두면 약관 페이지(/terms, 개인정보처리방침만 바뀌면 /privacy, 2days 참여자면 /2days/…)로 연결돼요.',
                )}
              >
                <input
                  type='url'
                  value={form.noticeUrl}
                  onChange={(e) => update({ noticeUrl: e.target.value })}
                  placeholder='https://ko-ready.com/terms'
                  className={INPUT_CLASS}
                />
              </Field>
            </Section>

            {errors.length > 0 && (
              <Banner tone='error'>
                <ul className='flex flex-col gap-[2px]'>
                  {errors.map(([en, ko]) => (
                    <li key={en}>· {t(en, ko)}</li>
                  ))}
                </ul>
              </Banner>
            )}

            <Section
              title={t('Send', '발송')}
              description={t(
                'Send a test to yourself first. Members who already received a campaign key are always skipped.',
                '먼저 나에게 테스트로 보내 확인해 보세요. 같은 발송 키로 이미 받은 회원은 항상 건너뛰어요.',
              )}
            >
              <div className='grid gap-[12px] tabletH:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'>
                <Field
                  label={t('Campaign key', '발송 키')}
                  hint={t(
                    'Blank = made from the effective date and documents.',
                    '비우면 시행일·개정 대상으로 자동 생성돼요.',
                  )}
                >
                  <input
                    type='text'
                    value={form.campaignKey}
                    onChange={(e) => update({ campaignKey: e.target.value })}
                    placeholder={
                      deriveCampaignKey(form) || 'policy-update-YYYY-MM-DD-terms'
                    }
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label={t('Send up to', '이번에 보낼 최대 인원')}
                  hint={t(
                    'Gmail allows 500/day (Workspace 2,000).',
                    'Gmail 하루 500통(Workspace 2,000통)',
                  )}
                >
                  <input
                    type='number'
                    min={1}
                    max={MAX_CAP}
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    onBlur={() => setCap(clampCap(cap))}
                    className={INPUT_CLASS}
                  />
                </Field>
              </div>

              <div className='flex flex-wrap items-center gap-x-[16px] gap-y-[4px] rounded-[10px] bg-grayscale-gray01 px-[14px] py-[10px] typo-b13 text-text-secondary'>
                {status ? (
                  <>
                    <span>
                      {t('Recipients', '전체 수신 대상')}{' '}
                      <strong className='text-text-primary'>
                        {status.totalRecipients.toLocaleString()}
                      </strong>
                    </span>
                    <span>
                      {t('Sent', '발송 완료')}{' '}
                      <strong className='text-text-primary'>
                        {status.sent.toLocaleString()}
                      </strong>
                    </span>
                    <span>
                      {t('Remaining', '남은 인원')}{' '}
                      <strong className='text-text-primary'>
                        {status.remaining.toLocaleString()}
                      </strong>
                    </span>
                    {status.unresolvedFailures > 0 && (
                      <span className='text-functional-failed03'>
                        {t('Failed, will retry', '실패 후 재시도 대기')}{' '}
                        {status.unresolvedFailures.toLocaleString()}
                      </span>
                    )}
                  </>
                ) : (
                  <span>
                    {t(
                      'Enter the effective date to see the send status.',
                      '시행일을 입력하면 발송 현황이 보여요.',
                    )}
                  </span>
                )}
              </div>

              {notice && <Banner tone={notice.tone}>{notice.text}</Banner>}

              <div className='flex flex-wrap gap-[8px] justify-between'>
                <BaseButton type='text_basic' size='m' hideIcon onClick={handleReset}>
                  {t('Start over', '새로 작성')}
                </BaseButton>
                <div className='flex flex-wrap gap-[8px]'>
                  <BaseButton
                    type='secondary'
                    size='m'
                    hideIcon
                    onClick={handleTestSend}
                    isLoading={busy === 'test'}
                    isDisabled={!!busy}
                  >
                    {t('Send test to me', '나에게 테스트 발송')}
                  </BaseButton>
                  <BaseButton
                    type='primary'
                    size='m'
                    hideIcon
                    onClick={handlePrepareSend}
                    isLoading={busy === 'prepare'}
                    isDisabled={!!busy}
                  >
                    {t('Send to all members', '전체 회원에게 발송')}
                  </BaseButton>
                </div>
              </div>
            </Section>
          </fieldset>

          {run && (
            <Section title={t('Sending progress', '발송 진행 상황')}>
              <div className='flex flex-col gap-[8px]'>
                <div className='h-[8px] w-full rounded-full bg-grayscale-gray02 overflow-hidden'>
                  <div
                    className='h-full rounded-full bg-functional-success03 transition-all duration-300'
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <p className='typo-b13 text-text-secondary'>
                  {t(
                    `Sent ${run.sent} of ${run.target} · failed ${run.failed} · ${run.remaining} members left in total`,
                    `${run.target}명 중 ${run.sent}명 발송 · 실패 ${run.failed}건 · 전체 남은 인원 ${run.remaining}명`,
                  )}
                </p>
              </div>

              {run.running ? (
                <div className='flex items-center justify-between gap-[12px]'>
                  <p className='typo-b12 text-text-placeholder'>
                    {t(
                      `Sending ${BATCH_SIZE} at a time. Keep this tab open.`,
                      `${BATCH_SIZE}명씩 나눠 보내는 중이에요. 이 탭을 닫지 마세요.`,
                    )}
                  </p>
                  <BaseButton
                    type='text_basic'
                    size='s'
                    hideIcon
                    onClick={() => {
                      stopRef.current = true;
                    }}
                  >
                    {t('Stop after this batch', '이번 묶음 후 중지')}
                  </BaseButton>
                </div>
              ) : (
                finished && <Banner tone={finished[0]}>{finished[1]}</Banner>
              )}

              {run.failedSamples.length > 0 && (
                <ul className='flex flex-col gap-[2px] typo-b12 text-text-secondary break-all'>
                  {run.failedSamples.map((sample, index) => (
                    <li key={index}>· {sample}</li>
                  ))}
                </ul>
              )}
            </Section>
          )}
        </div>

        <aside className='flex flex-col gap-[8px] min-w-0'>
          <div className='flex flex-col gap-[2px]'>
            <span className='typo-b13Strong text-text-secondary'>
              {t('Preview', '미리보기')}
            </span>
            <span className='typo-b14Strong text-text-primary break-all'>
              {preview?.subject}
            </span>
            {preview?.from && (
              <span className='typo-b12 text-text-secondary break-all'>
                {t('From', '보내는 사람')}: {preview.from}
              </span>
            )}
          </div>
          {previewFailed && (
            <Banner tone='error'>
              {t('Could not refresh the preview.', '미리보기를 새로 만들지 못했어요.')}
            </Banner>
          )}
          <iframe
            title={t('Email preview', '메일 미리보기')}
            srcDoc={preview?.html || ''}
            sandbox=''
            className='w-full h-[640px] desktopL:h-[calc(100vh-120px)] rounded-[12px] border border-grayscale-gray02 bg-grayscale-gray00'
          />
        </aside>
      </div>

      <SendPolicyNoticeModal
        isOpen={!!confirm}
        summary={confirm?.summary}
        onClose={() => setConfirm(null)}
        onConfirm={startSending}
      />
    </div>
  );
};

export default AdminPolicyNotice;
